import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { CodeEditorRef } from '@/components/code/CodeEditor';
import { libraries } from '@/lib/libraries';
import {
  PRIMITIVES,
  TRANSFORMS,
  BOOLEAN_OPS,
  ADVANCED_OPS,
  EXTRUDE_OPS,
  CLONE_OPS,
  SNIPPETS,
  MODIFIER_OPS,
  RENDER_SETTINGS,
  LIGHTS,
  SCENE,
  CAMERAS,
  SCENE_GRIDS,
  LIBRARY_FEATURES,
  PARAMETER_FEATURES,
  SCENE_CAMERA,
  CAMERA_PATH,
  SVG_OVERLAY,
  INTERACTION_FEATURES,
  MOTION_FEATURES,
  MESH_CLONE,
  MATERIAL_DEF,
  SHADER_MATERIAL,
  JS_SVG,
} from '@/lib/scad-constants';
import { ScadFeature } from '@/lib/scad-feature-types';
import { ScadFeatureDialog } from '@/components/code/ScadFeatureDialog';
import {
  Box,
  Circle,
  Cylinder,
  Layers,
  Minus,
  Move,
  Rotate3D,
  Maximize,
  Shrink,
  FlipHorizontal,
  Type,
  FileJson,
  ChevronDown,
  Code,
  Braces,
  FunctionSquare,
  Combine,
  Library,
  PanelLeftClose,
  Undo,
  Redo,
  Lightbulb,
  Palette,
  Paintbrush,
  Grid3x3,
  Globe,
  Bug,
  Ghost,
  Target,
  Ban,
  PackageX,
  Settings,
  Clapperboard,
  Sun,
  Camera,
  PictureInPicture2,
  Variable,
  SlidersHorizontal,
  ToggleLeft,
  AlignLeft,
  List,
  Dot,
  Grid2x2,
  Layers2,
  Hexagon,
  FileText,
  Eye,
  Lock,
  Radio,
  Zap,
  AlignJustify,
  MousePointerClick,
  Orbit,
  Copy,
  FileCode,
  Sparkles,
  Play,
} from 'lucide-react';

export interface ScadToolbarProps {
  editorRef: React.RefObject<CodeEditorRef | null>;
  onCollapse?: () => void;
  isMainFile?: boolean;
}

export function ScadToolbar({
  editorRef,
  onCollapse,
  isMainFile = true,
}: ScadToolbarProps) {
  const [activeFeature, setActiveFeature] = useState<ScadFeature | null>(null);

  if (!isMainFile) return null;

  const insertText = (text: string) => {
    editorRef.current?.insertText(text);
  };

  const wrapSelection = (before: string, after: string) => {
    editorRef.current?.wrapSelection(before, after);
  };

  const ensureLibraryImport = (libName: string) => {
    const currentCode = editorRef.current?.getValue() || '';
    let importCode = '';
    if (libName === 'BOSL2') {
      importCode = 'include <BOSL2/std.scad>\n';
    } else if (libName === 'MCAD') {
      importCode = 'include <MCAD/units.scad>\n';
    } else if (libName === 'LEGO') {
      importCode = 'use <LEGO/lego.scad>\n';
    } else if (libName === 'BOSL') {
      importCode = 'include <BOSL/constants.scad>\nuse <BOSL/shapes.scad>\n';
    } else if (libName === 'gridfinity') {
      importCode = 'include <gridfinity/gridfinity-rebuilt-bins.scad>\n';
    } else {
      importCode = `include <${libName}/std.scad>\n`;
    }

    if (!currentCode.includes(`<${libName}/`)) {
      editorRef.current?.insertAtTop(importCode);
    }
  };

  const handleLibraryFeatureClick = (feature: ScadFeature) => {
    if (feature.library) ensureLibraryImport(feature.library);
    handleFeatureClick(feature);
  };

  const handleFeatureClick = (feature: ScadFeature) => {
    if (feature.fields && feature.fields.length > 0) {
      setActiveFeature(feature);
    } else if (feature.wrap) {
      wrapSelection(feature.before || '', feature.after || '');
    } else if (feature.code) {
      insertText(feature.code);
    }
  };

  const handleDialogSubmit = (
    result: string | { before: string; after: string },
  ) => {
    if (activeFeature?.library) {
      ensureLibraryImport(activeFeature.library);
    }

    if (typeof result === 'string') {
      insertText(result);
    } else {
      wrapSelection(result.before, result.after);
    }
  };

  return (
    <>
      <div className="flex h-12 items-center justify-between border-b border-border px-2">
        <div className="flex items-center gap-0.5">
          {/* Collapse Button */}
          {onCollapse && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={onCollapse}
                >
                  <PanelLeftClose className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Collapse panel</TooltipContent>
            </Tooltip>
          )}

          <span className="text-sm font-medium text-foreground">Code</span>
        </div>

        <div className="flex items-center gap-0.5">
          {/* Undo/Redo */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => editorRef.current?.undo()}
              >
                <Undo className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Undo</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => editorRef.current?.redo()}
              >
                <Redo className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Redo</TooltipContent>
          </Tooltip>

          <div className="mx-1 h-4 w-px bg-border" />

          {/* ================================================================
              MENU 1 — SHAPE
              Primitives · Booleans · Transforms · Extrude
              ================================================================ */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-xs"
                    >
                      <Box className="h-3.5 w-3.5" />
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    {/* 3D Primitives */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Box className="mr-2 h-4 w-4" />
                        3D Primitives
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-40">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(PRIMITIVES.cube as ScadFeature)
                          }
                        >
                          <Box className="mr-2 h-4 w-4" />
                          {PRIMITIVES.cube.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PRIMITIVES.sphere as ScadFeature,
                            )
                          }
                        >
                          <Circle className="mr-2 h-4 w-4" />
                          {PRIMITIVES.sphere.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PRIMITIVES.cylinder as ScadFeature,
                            )
                          }
                        >
                          <Cylinder className="mr-2 h-4 w-4" />
                          {PRIMITIVES.cylinder.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* 2D Primitives */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Circle className="mr-2 h-4 w-4" />
                        2D Primitives
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-40">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PRIMITIVES.circle as ScadFeature,
                            )
                          }
                        >
                          <Circle className="mr-2 h-4 w-4" />
                          {PRIMITIVES.circle.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PRIMITIVES.square as ScadFeature,
                            )
                          }
                        >
                          <Box className="mr-2 h-4 w-4" />
                          {PRIMITIVES.square.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(PRIMITIVES.text as ScadFeature)
                          }
                        >
                          <Type className="mr-2 h-4 w-4" />
                          {PRIMITIVES.text.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSeparator />

                    {/* Boolean Operations */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Combine className="mr-2 h-4 w-4" />
                        Boolean Ops
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-44">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(BOOLEAN_OPS.union as ScadFeature)
                          }
                        >
                          <Layers className="mr-2 h-4 w-4" />
                          Union
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              BOOLEAN_OPS.difference as ScadFeature,
                            )
                          }
                        >
                          <Minus className="mr-2 h-4 w-4" />
                          Difference
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              BOOLEAN_OPS.intersection as ScadFeature,
                            )
                          }
                        >
                          <Combine className="mr-2 h-4 w-4" />
                          Intersection
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(ADVANCED_OPS.hull as ScadFeature)
                          }
                        >
                          <Layers className="mr-2 h-4 w-4" />
                          Hull
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              ADVANCED_OPS.minkowski as ScadFeature,
                            )
                          }
                        >
                          <Box className="mr-2 h-4 w-4" />
                          Minkowski
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Transforms */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Move className="mr-2 h-4 w-4" />
                        Transforms
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-40">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              TRANSFORMS.translate as ScadFeature,
                            )
                          }
                        >
                          <Move className="mr-2 h-4 w-4" />
                          Translate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              TRANSFORMS.rotate as ScadFeature,
                            )
                          }
                        >
                          <Rotate3D className="mr-2 h-4 w-4" />
                          Rotate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(TRANSFORMS.scale as ScadFeature)
                          }
                        >
                          <Maximize className="mr-2 h-4 w-4" />
                          Scale
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              TRANSFORMS.resize as ScadFeature,
                            )
                          }
                        >
                          <Shrink className="mr-2 h-4 w-4" />
                          Resize
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              TRANSFORMS.mirror as ScadFeature,
                            )
                          }
                        >
                          <FlipHorizontal className="mr-2 h-4 w-4" />
                          Mirror
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSeparator />

                    {/* Extrude */}
                    <DropdownMenuLabel>Extrude</DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() =>
                        handleFeatureClick(
                          EXTRUDE_OPS.linear_extrude as ScadFeature,
                        )
                      }
                    >
                      Linear Extrude
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        handleFeatureClick(
                          EXTRUDE_OPS.rotate_extrude as ScadFeature,
                        )
                      }
                    >
                      Rotate Extrude
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            </TooltipTrigger>
            <TooltipContent>Shape — primitives, booleans, transforms, extrude</TooltipContent>
          </Tooltip>

          {/* ================================================================
              MENU 2 — SCENE
              Colors & Materials · Lights · Scene Settings · Grids · Overlays & Cameras
              ================================================================ */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-xs"
                    >
                      <Clapperboard className="h-3.5 w-3.5" />
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">

                    {/* Colors & Materials */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Palette className="mr-2 h-4 w-4" />
                        Colors & Materials
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(TRANSFORMS.color as ScadFeature)
                          }
                        >
                          <Palette className="mr-2 h-4 w-4" />
                          Color
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              MATERIAL_DEF.material_def as ScadFeature,
                            )
                          }
                        >
                          <Sparkles className="mr-2 h-4 w-4" />
                          {MATERIAL_DEF.material_def.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              TRANSFORMS.material as ScadFeature,
                            )
                          }
                        >
                          <Paintbrush className="mr-2 h-4 w-4" />
                          Material (apply PBR)
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SHADER_MATERIAL.shader_material as ScadFeature,
                            )
                          }
                        >
                          <FileCode className="mr-2 h-4 w-4" />
                          {SHADER_MATERIAL.shader_material.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Lights */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Sun className="mr-2 h-4 w-4" />
                        Lights
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(LIGHTS.ambient as ScadFeature)
                          }
                        >
                          <Lightbulb className="mr-2 h-4 w-4" />
                          {LIGHTS.ambient.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              LIGHTS.directional as ScadFeature,
                            )
                          }
                        >
                          <Sun className="mr-2 h-4 w-4" />
                          {LIGHTS.directional.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(LIGHTS.point as ScadFeature)
                          }
                        >
                          <Lightbulb className="mr-2 h-4 w-4" />
                          {LIGHTS.point.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(LIGHTS.spot as ScadFeature)
                          }
                        >
                          <Lightbulb className="mr-2 h-4 w-4" />
                          {LIGHTS.spot.label}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SCENE.scene_default_lights as ScadFeature,
                            )
                          }
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          {SCENE.scene_default_lights.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Scene Settings */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Settings className="mr-2 h-4 w-4" />
                        Scene Settings
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SCENE.scene_material as ScadFeature,
                            )
                          }
                        >
                          <Paintbrush className="mr-2 h-4 w-4" />
                          {SCENE.scene_material.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SCENE.scene_shadows as ScadFeature,
                            )
                          }
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          {SCENE.scene_shadows.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(SCENE.scene_bloom as ScadFeature)
                          }
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          {SCENE.scene_bloom.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(SCENE.scene_ssao as ScadFeature)
                          }
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          {SCENE.scene_ssao.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SCENE.scene_background as ScadFeature,
                            )
                          }
                        >
                          <Palette className="mr-2 h-4 w-4" />
                          {SCENE.scene_background.label}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SCENE_CAMERA.scene_camera as ScadFeature,
                            )
                          }
                        >
                          <Camera className="mr-2 h-4 w-4" />
                          {SCENE_CAMERA.scene_camera.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Grids & Backgrounds */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Grid3x3 className="mr-2 h-4 w-4" />
                        Grids & Backgrounds
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-48">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SCENE_GRIDS.plane_grid as ScadFeature,
                            )
                          }
                        >
                          <Grid3x3 className="mr-2 h-4 w-4" />
                          {SCENE_GRIDS.plane_grid.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SCENE_GRIDS.cube_wireframe as ScadFeature,
                            )
                          }
                        >
                          <Box className="mr-2 h-4 w-4" />
                          {SCENE_GRIDS.cube_wireframe.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SCENE_GRIDS.sphere_wireframe as ScadFeature,
                            )
                          }
                        >
                          <Globe className="mr-2 h-4 w-4" />
                          {SCENE_GRIDS.sphere_wireframe.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Overlays & Cameras */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <PictureInPicture2 className="mr-2 h-4 w-4" />
                        Overlays & Cameras
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              SVG_OVERLAY.svg_overlay as ScadFeature,
                            )
                          }
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          {SVG_OVERLAY.svg_overlay.label}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(CAMERAS.pip as ScadFeature)
                          }
                        >
                          <Camera className="mr-2 h-4 w-4" />
                          {CAMERAS.pip.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(CAMERAS.pip_ortho as ScadFeature)
                          }
                        >
                          <PictureInPicture2 className="mr-2 h-4 w-4" />
                          {CAMERAS.pip_ortho.label}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(CAMERA_PATH.camera_path as ScadFeature)
                          }
                        >
                          <Play className="mr-2 h-4 w-4" />
                          {CAMERA_PATH.camera_path.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            </TooltipTrigger>
            <TooltipContent>Scene — materials, lights, settings, grids, cameras</TooltipContent>
          </Tooltip>

          {/* ================================================================
              MENU 3 — CODE
              Libraries · Snippets · Modifiers · Render Settings · JS SVG
              ================================================================ */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-xs"
                    >
                      <Code className="h-3.5 w-3.5" />
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-52"
                  >
                    {/* Libraries submenu */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Library className="mr-2 h-4 w-4" />
                        Libraries
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="max-h-96 w-56 overflow-y-auto">
                        {libraries.map((lib) => (
                          <DropdownMenuGroup key={lib.name}>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="flex items-center text-xs text-muted-foreground">
                              <Library className="mr-2 h-3 w-3" />
                              {(
                                'displayName' in lib
                                  ? lib.displayName
                                  : lib.name
                              ) as string}
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => ensureLibraryImport(lib.name)}
                              className="text-xs font-semibold"
                            >
                              Include{' '}
                              {(
                                'displayName' in lib
                                  ? lib.displayName
                                  : lib.name
                              ) as string}
                            </DropdownMenuItem>
                            {LIBRARY_FEATURES[lib.name]?.map((feat) => (
                              <DropdownMenuItem
                                key={feat.id}
                                onClick={() =>
                                  handleLibraryFeatureClick(feat as ScadFeature)
                                }
                                className="pl-6 text-xs"
                              >
                                <Box className="mr-2 h-3 w-3" />
                                {feat.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuGroup>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSeparator />

                    {/* Snippets */}
                    <DropdownMenuLabel>Snippets</DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => insertText(SNIPPETS.module.code)}
                    >
                      <Braces className="mr-2 h-4 w-4" />
                      Module
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => insertText(SNIPPETS.function.code)}
                    >
                      <FunctionSquare className="mr-2 h-4 w-4" />
                      Function
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => insertText(SNIPPETS.for_loop.code)}
                    >
                      <Code className="mr-2 h-4 w-4" />
                      For Loop
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => insertText(SNIPPETS.if_else.code)}
                    >
                      <Code className="mr-2 h-4 w-4" />
                      If / Else
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => insertText(SNIPPETS.polygon.code)}
                    >
                      <Box className="mr-2 h-4 w-4" />
                      Polygon
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => insertText(SNIPPETS.polyhedron.code)}
                    >
                      <Box className="mr-2 h-4 w-4" />
                      Polyhedron
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => insertText(SNIPPETS.parameters.code)}
                    >
                      <FileJson className="mr-2 h-4 w-4" />
                      Parameter Block
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {/* Modifiers submenu */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Bug className="mr-2 h-4 w-4" />
                        Modifiers
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-48">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              MODIFIER_OPS.debug as ScadFeature,
                            )
                          }
                        >
                          <Bug className="mr-2 h-4 w-4" />
                          {MODIFIER_OPS.debug.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              MODIFIER_OPS.background as ScadFeature,
                            )
                          }
                        >
                          <Ghost className="mr-2 h-4 w-4" />
                          {MODIFIER_OPS.background.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(MODIFIER_OPS.root as ScadFeature)
                          }
                        >
                          <Target className="mr-2 h-4 w-4" />
                          {MODIFIER_OPS.root.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              MODIFIER_OPS.disable as ScadFeature,
                            )
                          }
                        >
                          <Ban className="mr-2 h-4 w-4" />
                          {MODIFIER_OPS.disable.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              MODIFIER_OPS.exclude_from_export as ScadFeature,
                            )
                          }
                        >
                          <PackageX className="mr-2 h-4 w-4" />
                          {MODIFIER_OPS.exclude_from_export.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Render Settings submenu */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Settings className="mr-2 h-4 w-4" />
                        Render Settings
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-44">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              RENDER_SETTINGS.render_fn as ScadFeature,
                            )
                          }
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          {RENDER_SETTINGS.render_fn.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              RENDER_SETTINGS.render_fa as ScadFeature,
                            )
                          }
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          {RENDER_SETTINGS.render_fa.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              RENDER_SETTINGS.render_fs as ScadFeature,
                            )
                          }
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          {RENDER_SETTINGS.render_fs.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSeparator />

                    {/* JS SVG */}
                    <DropdownMenuItem
                      onClick={() =>
                        handleFeatureClick(JS_SVG.js_svg as ScadFeature)
                      }
                    >
                      <FileCode className="mr-2 h-4 w-4" />
                      {JS_SVG.js_svg.label}
                    </DropdownMenuItem>

                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            </TooltipTrigger>
            <TooltipContent>Code — libraries, snippets, modifiers, render settings</TooltipContent>
          </Tooltip>

          {/* ================================================================
              MENU 4 — INTERACT
              Clone/Matrix · Mesh Clone · Motion · Interactive · Annotation
              ================================================================ */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-xs"
                    >
                      <MousePointerClick className="h-3.5 w-3.5" />
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">

                    {/* Clone / Matrix */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Layers className="mr-2 h-4 w-4" />
                        Clone / Matrix
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-48">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(CLONE_OPS.clone_1d as ScadFeature)
                          }
                        >
                          1D Clone (Line)
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(CLONE_OPS.clone_2d as ScadFeature)
                          }
                        >
                          2D Clone (Grid)
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(CLONE_OPS.clone_3d as ScadFeature)
                          }
                        >
                          3D Clone (Cube)
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Mesh Clone */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Copy className="mr-2 h-4 w-4" />
                        Mesh Clone
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              MESH_CLONE.clone_ref as ScadFeature,
                            )
                          }
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          {MESH_CLONE.clone_ref.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(MESH_CLONE.clone as ScadFeature)
                          }
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          {MESH_CLONE.clone.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSeparator />

                    {/* Motion */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Orbit className="mr-2 h-4 w-4" />
                        Motion
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              MOTION_FEATURES.motion_translate as ScadFeature,
                            )
                          }
                        >
                          <Move className="mr-2 h-4 w-4" />
                          {MOTION_FEATURES.motion_translate.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              MOTION_FEATURES.motion_orbit as ScadFeature,
                            )
                          }
                        >
                          <Orbit className="mr-2 h-4 w-4" />
                          {MOTION_FEATURES.motion_orbit.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              MOTION_FEATURES.motion_spin as ScadFeature,
                            )
                          }
                        >
                          <Rotate3D className="mr-2 h-4 w-4" />
                          {MOTION_FEATURES.motion_spin.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSeparator />

                    {/* Interactive */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <MousePointerClick className="mr-2 h-4 w-4" />
                        Interactive
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-56">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              INTERACTION_FEATURES.interactive as ScadFeature,
                            )
                          }
                        >
                          <MousePointerClick className="mr-2 h-4 w-4" />
                          {INTERACTION_FEATURES.interactive.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              INTERACTION_FEATURES.interactive_publish as ScadFeature,
                            )
                          }
                        >
                          <Zap className="mr-2 h-4 w-4" />
                          {INTERACTION_FEATURES.interactive_publish.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Annotation */}
                    <DropdownMenuItem
                      onClick={() =>
                        handleFeatureClick(
                          INTERACTION_FEATURES.annotation as ScadFeature,
                        )
                      }
                    >
                      <AlignLeft className="mr-2 h-4 w-4" />
                      {INTERACTION_FEATURES.annotation.label}
                    </DropdownMenuItem>

                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            </TooltipTrigger>
            <TooltipContent>Interact — clones, motion, interactive, annotation</TooltipContent>
          </Tooltip>

          {/* ================================================================
              MENU 5 — PARAMETERS
              (same submenus as before)
              ================================================================ */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-xs"
                    >
                      <Variable className="h-3.5 w-3.5" />
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">

                    {/* Numbers */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <SlidersHorizontal className="mr-2 h-4 w-4" />
                        Numbers
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.number_slider as ScadFeature,
                            )
                          }
                        >
                          <SlidersHorizontal className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.number_slider.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.number_slider_range as ScadFeature,
                            )
                          }
                        >
                          <SlidersHorizontal className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.number_slider_range.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.number_slider_step as ScadFeature,
                            )
                          }
                        >
                          <SlidersHorizontal className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.number_slider_step.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.number_slider_extended_range as ScadFeature,
                            )
                          }
                        >
                          <SlidersHorizontal className="mr-2 h-4 w-4" />
                          {
                            PARAMETER_FEATURES.number_slider_extended_range
                              .label
                          }
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.angle_dial as ScadFeature,
                            )
                          }
                        >
                          <Rotate3D className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.angle_dial.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.stepper as ScadFeature,
                            )
                          }
                        >
                          <List className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.stepper.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.range_input as ScadFeature,
                            )
                          }
                        >
                          <Dot className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.range_input.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Text & Toggles */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <AlignLeft className="mr-2 h-4 w-4" />
                        Text & Toggles
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-48">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.string_input as ScadFeature,
                            )
                          }
                        >
                          <AlignLeft className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.string_input.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.boolean_toggle as ScadFeature,
                            )
                          }
                        >
                          <ToggleLeft className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.boolean_toggle.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.font_picker as ScadFeature,
                            )
                          }
                        >
                          <Type className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.font_picker.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.file_upload as ScadFeature,
                            )
                          }
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.file_upload.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Dropdowns */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <List className="mr-2 h-4 w-4" />
                        Dropdowns
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.number_dropdown as ScadFeature,
                            )
                          }
                        >
                          <List className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.number_dropdown.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.labelled_dropdown as ScadFeature,
                            )
                          }
                        >
                          <List className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.labelled_dropdown.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.string_dropdown as ScadFeature,
                            )
                          }
                        >
                          <List className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.string_dropdown.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Colors */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Palette className="mr-2 h-4 w-4" />
                        Colors
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.color_picker as ScadFeature,
                            )
                          }
                        >
                          <Palette className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.color_picker.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.color_picker_alpha as ScadFeature,
                            )
                          }
                        >
                          <Palette className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.color_picker_alpha.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.color_swatches as ScadFeature,
                            )
                          }
                        >
                          <Palette className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.color_swatches.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Vectors & Pads */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Grid2x2 className="mr-2 h-4 w-4" />
                        Vectors & Pads
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-44">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.vector_sliders as ScadFeature,
                            )
                          }
                        >
                          <SlidersHorizontal className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.vector_sliders.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.xy_pad as ScadFeature,
                            )
                          }
                        >
                          <Grid2x2 className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.xy_pad.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.xyz_pad as ScadFeature,
                            )
                          }
                        >
                          <Grid2x2 className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.xyz_pad.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.polygon_pad as ScadFeature,
                            )
                          }
                        >
                          <Hexagon className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.polygon_pad.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Special Inputs */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Grid3x3 className="mr-2 h-4 w-4" />
                        Special Inputs
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-48">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.grid_input as ScadFeature,
                            )
                          }
                        >
                          <Grid3x3 className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.grid_input.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.stack_input as ScadFeature,
                            )
                          }
                        >
                          <Layers2 className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.stack_input.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.svg_checkbox as ScadFeature,
                            )
                          }
                        >
                          <Radio className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.svg_checkbox.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.svg_radio as ScadFeature,
                            )
                          }
                        >
                          <Radio className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.svg_radio.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuSeparator />

                    {/* Organization */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <AlignJustify className="mr-2 h-4 w-4" />
                        Organization
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-48">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.parameter_groups as ScadFeature,
                            )
                          }
                        >
                          <AlignJustify className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.parameter_groups.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.tab_groups as ScadFeature,
                            )
                          }
                        >
                          <AlignJustify className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.tab_groups.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.stop_parsing as ScadFeature,
                            )
                          }
                        >
                          <Minus className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.stop_parsing.label}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.markdown_block as ScadFeature,
                            )
                          }
                        >
                          <AlignLeft className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.markdown_block.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.snapshots as ScadFeature,
                            )
                          }
                        >
                          <Camera className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.snapshots.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    {/* Annotations */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Eye className="mr-2 h-4 w-4" />
                        Annotations
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-52">
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.visibility_expr as ScadFeature,
                            )
                          }
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.visibility_expr.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.lock_annotation as ScadFeature,
                            )
                          }
                        >
                          <Lock className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.lock_annotation.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleFeatureClick(
                              PARAMETER_FEATURES.pubsub_annotation as ScadFeature,
                            )
                          }
                        >
                          <Zap className="mr-2 h-4 w-4" />
                          {PARAMETER_FEATURES.pubsub_annotation.label}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>

                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            </TooltipTrigger>
            <TooltipContent>Parameters — all parameter widget types</TooltipContent>
          </Tooltip>

        </div>
      </div>

      <ScadFeatureDialog
        feature={activeFeature}
        isOpen={activeFeature !== null}
        onClose={() => setActiveFeature(null)}
        onSubmit={handleDialogSubmit}
      />
    </>
  );
}
